<template>
  <div id="orgArea" class="areas-container">
    <area-node :node="areaTree" :level="0" />
  </div>
</template>
<script>
import AreaNode from "./AreaNode.vue";
export default {
  name: "OrgArea",
  props: {
    parent: { type: Object, required: true },
  },
  data() {
    return {
      areaColors: [
        "#FFB6C1",
        "#B0E0E6",
        "#FFD700",
        "#90EE90",
        "#FFA07A",
        "#DDA0DD",
        "#87CEFA",
      ],
    };
  },
  computed: {
    areaTree() {
      return this.buildAreaHierarchy(this.parent);
    },
  },
  methods: {
    buildAreaTree(node) {
      if (node.children && node.children.length > 0) {
        return {
          name: node.name,
          value: node.value,
          type: "area",
          children: node.children.map((child) => this.buildAreaTree(child)),
        };
      } else {
        return {
          name: node.name,
          value: node.value,
          type: "puesto",
        };
      }
    },
    buildAreaHierarchy(root) {
      const areaMap = {};
      function collect(node, path = new Set()) {
        // path para detectar ciclos en collect
        if (!node) return;
        if (path.has(node.value)) {
          // Usar un identificador único del nodo, ej: node.value o node.PstID
          console.warn(
            "Ciclo detectado en 'collect' para el nodo:",
            node.name,
            ". Omitiendo."
          );
          return;
        }
        path.add(node.value);

        if (!node.area) node.area = node.AreName || node.name;
        if (!areaMap[node.area]) {
          areaMap[node.area] = {
            AreName: node.area,
            puestos: [],
            subareas: new Set(),
            parentArea: null,
            level: node.level, // Tomar el nivel del primer puesto que define el área
          };
        }
        areaMap[node.area].puestos.push(node);
        // Si el nivel del nodo actual es menor, actualizar el nivel del área (para el puesto principal)
        if (node.level < areaMap[node.area].level) {
          areaMap[node.area].level = node.level;
        }

        if (node.children && node.children.length) {
          node.children.forEach((child) => {
            collect(child, new Set(path)); // Pasar una copia del path
            if (child.area && child.area !== node.area) {
              if (areaMap[node.area] && areaMap[child.area]) {
                // Asegurarse que ambas áreas existan
                areaMap[node.area].subareas.add(child.area);
                areaMap[child.area].parentArea = node.area;
              } else {
                console.warn(
                  `Área del padre (${node.area}) o del hijo (${child.area}) no encontrada en areaMap al procesar hijos de:`,
                  node.name
                );
              }
            }
          });
        }
        path.delete(node.value); // Limpiar el nodo del path actual al salir de su recursión
      }
      collect(root);

      const visitedInBuildTree = new Set();
      function buildTree(areaName) {
        if (visitedInBuildTree.has(areaName)) {
          console.error(
            `Ciclo detectado al intentar construir el árbol para el área: ${areaName}.`
          );
          return {
            AreName: areaName + " (CICLO DETECTADO)",
            type: "area",
            level: 0,
            mainPosition: "",
            puestos: [],
            children: [],
          };
        }
        visitedInBuildTree.add(areaName);

        const areaData = areaMap[areaName];
        if (!areaData) {
          console.error(`Área "${areaName}" no encontrada en areaMap.`);
          visitedInBuildTree.delete(areaName);
          return {
            AreName: areaName + " (NO ENCONTRADA)",
            type: "area",
            level: 0,
            mainPosition: "",
            puestos: [],
            children: [],
          };
        }

        // Determinar el puesto principal (el de menor nivel dentro del área)
        let mainPuesto = null;
        if (areaData.puestos && areaData.puestos.length > 0) {
          mainPuesto = areaData.puestos.reduce((prev, curr) =>
            prev.level < curr.level ? prev : curr
          );
        }

        const childrenNodes = Array.from(areaData.subareas).map((subAreaName) =>
          buildTree(subAreaName)
        );

        visitedInBuildTree.delete(areaName);

        return {
          AreName: areaData.AreName,
          type: "area",
          level: mainPuesto ? mainPuesto.level : areaData.level || 0,
          mainPosition: mainPuesto ? mainPuesto.name : "",
          puestos: areaData.puestos
            ? areaData.puestos.filter((p) => p.area === areaData.AreName)
            : [],
          children: childrenNodes,
        };
      }

      const rootAreaEntry = Object.values(areaMap).find((a) => !a.parentArea);
      if (!rootAreaEntry) {
        console.error(
          "No se pudo encontrar un área raíz (sin parentArea). Verifique la lógica de 'collect' y los datos de entrada."
        );
        // Como fallback, podrías intentar tomar la primera área o la de menor nivel si no hay una raíz clara.
        // O devolver una estructura de error.
        const areaNames = Object.keys(areaMap);
        if (areaNames.length > 0) {
          console.warn(
            `Intentando usar "${areaNames[0]}" como raíz de fallback.`
          );
          return buildTree(areaNames[0]);
        }
        return {
          AreName: "ERROR: NO HAY RAÍZ",
          type: "area",
          level: 0,
          mainPosition: "",
          puestos: [],
          children: [],
        };
      }
      return buildTree(rootAreaEntry.AreName);
    },
  },
  components: {
    AreaNode,
  },
};
</script>
